import { format as formatDateFns, getYear, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { AggregationMode } from '~/packages/variable/variables.typing'
import { readMeta, resolveSeriesYear } from './extract'
import { formatBR, stripParens } from './format'
import { usesMonthYearShortLabel } from './titles'
import type { SeriesNormalized, TimeSeriesComparisonPayload } from '../visualization.types'

export interface ComparisonBarRow {
  key: string
  leftLabel: string
  value: number
  valueText: string
  color: string
  title: string
}

export interface ComparisonBarsModel {
  rows: ComparisonBarRow[]
  maxAbsValue: number
  shouldUseMonthYearLabelOnLeft: boolean
}

type RowBuildMode = 'primary' | 'fallback'

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const isZero = (value: number) => Math.abs(value) < 1e-9

const extractTrailingParenthetical = (text?: string | null) => {
  if (!text) return null
  const match = text.match(/\(([^()]*)\)\s*$/)
  return match?.[1]?.trim() || null
}

const isInvalidDerivedValueText = (text?: string | null) => {
  if (!text) return true
  const normalized = text.trim().toLowerCase()
  return (
    normalized.length === 0 ||
    normalized === 'nan' ||
    normalized === 'NaN' ||
    normalized === '—' ||
    normalized === '-' ||
    normalized === 'null' ||
    normalized === 'undefined'
  )
}

const formatBarLabel = (label: string | undefined, year: number) => {
  if (!label) return String(year)
  return label.includes(String(year)) ? label : `${label} ${year}`
}

const toMonthYearShortLabel = (baseDate: Date | undefined, year: number, label?: string) => {
  if (baseDate && !Number.isNaN(baseDate.getTime())) {
    const monthShort = formatDateFns(baseDate, 'MMM', { locale: ptBR }).replace('.', '').trim()
    if (monthShort) {
      const month = monthShort.charAt(0).toUpperCase() + monthShort.slice(1).toLowerCase()
      return `${month}/${baseDate.getFullYear()}`
    }
  }

  if (!label) return undefined
  const firstToken = label
    .replace(/\d{4}/g, '')
    .replace(/\bde\b/gi, '')
    .trim()
    .split(/\s+/)[0]
  if (!firstToken) return undefined

  const month =
    firstToken.slice(0, 3).charAt(0).toUpperCase() + firstToken.slice(1, 3).toLowerCase()
  return `${month}/${year}`
}

const resolveAggregationMode = (payload: TimeSeriesComparisonPayload): AggregationMode => {
  const raw = payload?.meta?.aggregationMode ?? payload?.meta?.aggregation
  if (raw === 'mean' || raw === 'median' || raw === 'max' || raw === 'min') return raw
  return 'sum'
}

const aggregateValues = (values: number[], aggregationMode: AggregationMode): number | null => {
  const finiteValues = values.filter(isFiniteNumber)
  if (!finiteValues.length) return null

  switch (aggregationMode) {
    case 'mean':
      return finiteValues.reduce((acc, value) => acc + value, 0) / finiteValues.length
    case 'median': {
      const sorted = finiteValues.slice().sort((a, b) => a - b)
      const mid = Math.floor(sorted.length / 2)
      return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
    }
    case 'max':
      return Math.max(...finiteValues)
    case 'min':
      return Math.min(...finiteValues)
    case 'sum':
    default:
      return finiteValues.reduce((acc, value) => acc + value, 0)
  }
}

const aggregateSlice = (
  values: number[],
  aggregationMode: AggregationMode,
  alignedDays?: number,
): number | null => {
  const slice =
    typeof alignedDays === 'number' && alignedDays > 0 ? values.slice(0, alignedDays) : values
  return aggregateValues(slice, aggregationMode)
}

export const hasInsufficientVariationData = (payload: TimeSeriesComparisonPayload) => {
  const subtitleNormalized = payload?.subtitle?.toLowerCase() ?? ''
  return (
    payload?.meta?.percentageChange === null ||
    subtitleNormalized.includes('não há dados suficientes para calcular a variação') ||
    subtitleNormalized.includes('nao ha dados suficientes para calcular a variacao')
  )
}

export function buildComparisonBarsModel(
  payload: TimeSeriesComparisonPayload,
  series: SeriesNormalized[],
  previewTitle?: string,
): ComparisonBarsModel | null {
  if (!Array.isArray(series) || series.length === 0) return null

  const refISO: string =
    payload?.meta?.reference?.start ||
    payload?.reference?.start ||
    new Date().toISOString().slice(0, 10)
  const comparisonISO: string | undefined =
    payload?.meta?.comparison?.start || payload?.comparison?.start

  const refDate = parseISO(refISO)
  const comparisonDate = comparisonISO ? parseISO(comparisonISO) : undefined
  const fallbackRefYear = getYear(refDate)
  const meta = readMeta(payload)
  const aggregationMode = resolveAggregationMode(payload)
  const shouldUseMonthYearLabelOnLeft = usesMonthYearShortLabel(
    previewTitle ?? payload?.title ?? payload?.meta?.labels?.title,
  )

  const minDays = Math.min(series[0]?.values.length ?? 0, series[1]?.values.length ?? 0)
  const maxSeriesDays = Math.max(...series.map(serie => serie.values?.length ?? 0), 0)
  const alignedDays = minDays > 0 ? minDays : undefined
  const partialDaysInfo =
    typeof alignedDays === 'number' && alignedDays < maxSeriesDays
      ? `${alignedDays} dias`
      : undefined

  const buildRow = (index: number, mode: RowBuildMode): ComparisonBarRow | null => {
    const current = series[index]
    if (!current) return null

    const year = resolveSeriesYear(current, index, meta, fallbackRefYear)
    const label = stripParens(current.labelRaw || current.name)
    const baseDate = index === 0 ? comparisonDate : refDate
    const metaValue =
      index === 0 ? payload?.meta?.comparison?.value : payload?.meta?.reference?.value
    const fallbackValue = aggregateSlice(
      current.values ?? [],
      aggregationMode,
      mode === 'primary' ? alignedDays : undefined,
    )
    const value = isFiniteNumber(metaValue) && mode === 'primary' ? metaValue : fallbackValue
    if (!isFiniteNumber(value)) return null

    const derivedValueText =
      mode === 'primary' ? extractTrailingParenthetical(current.labelRaw || current.name) : null
    const valueText = !isInvalidDerivedValueText(derivedValueText)
      ? derivedValueText!
      : formatBR(value)
    const valueCaption = aggregationMode === 'sum' && !valueText.includes('%') ? 'Total' : 'Valor'
    const leftLabel = shouldUseMonthYearLabelOnLeft
      ? (toMonthYearShortLabel(baseDate, year, label ?? undefined) ?? String(year))
      : String(year)

    return {
      key: `${index}-${leftLabel}-${mode}`,
      leftLabel,
      value,
      valueText,
      color: current.color ?? (index === 0 ? '#2563eb' : '#9ca3af'),
      title: `${formatBarLabel(label ?? '', year)}${
        mode === 'primary' && partialDaysInfo ? ` (${partialDaysInfo})` : ''
      }\n${valueCaption}: ${valueText}`,
    }
  }

  const primaryRows = [0, 1]
    .map(index => buildRow(index, 'primary'))
    .filter((row): row is ComparisonBarRow => Boolean(row))
  const primaryHasSignal = primaryRows.some(row => !isZero(row.value))

  const rows =
    primaryHasSignal || !hasInsufficientVariationData(payload)
      ? primaryRows
      : [0, 1]
          .map(index => buildRow(index, 'fallback'))
          .filter((row): row is ComparisonBarRow => Boolean(row))

  if (!rows.length) return null
  if (rows.every(row => isZero(row.value)) && hasInsufficientVariationData(payload)) return null

  return {
    rows,
    maxAbsValue: Math.max(...rows.map(row => Math.abs(row.value)), 0),
    shouldUseMonthYearLabelOnLeft,
  }
}
